import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { getSupportErrorCode } from '@api/SupportApi';

import { useSupportRealtime } from './useSupportRealtime';
import { supportQueryOptions } from '../queries/supportQueries';
import { DEFAULT_TICKET_LIST_FILTER, SupportStatusFilter, SupportTicketListFilter } from '../utils/ticketListFilter';

export interface SupportInbox {
    selectedTicketId?: string;
    selectedTicketUnread: number;
    totalUnread: number;
    filter: SupportTicketListFilter;
    searchText: string;
    changeSearchText(searchText: string): void;
    changeStatusFilter(status: SupportStatusFilter): void;
    resetFilter(): void;
    selectTicket(ticketId: string): void;
    closeTicket(): void;
}

const SEARCH_DEBOUNCE_MS = 300;

export function useSupportInbox(): SupportInbox {
    const [selectedTicketId, setSelectedTicketId] = useState<string>();
    const [filter, setFilter] = useState<SupportTicketListFilter>(DEFAULT_TICKET_LIST_FILTER);
    const [searchText, setSearchText] = useState('');
    const ticketsQuery = useInfiniteQuery({
        ...supportQueryOptions.ticketList(filter),
    });
    const { data: totalUnread = 0 } = useQuery(supportQueryOptions.totalUnread());
    useSupportRealtime({
        filter,
        selectedTicketId,
        onTicketRemoved: ticketId => {
            if (selectedTicketId === ticketId) {
                store.app.showPopup({
                    title: translate('supportTitle'),
                    description: translate('supportAccessRemoved'),
                });
            }
            setSelectedTicketId(currentTicketId => (currentTicketId === ticketId ? undefined : currentTicketId));
        },
        onAuthorizationRequired: () =>
            store.app.showPopup({
                title: translate('supportTitle'),
                description: translate('supportAuthorizationRequired'),
            }),
    });

    useEffect(() => {
        const timer = setTimeout(() => setFilter(current => applySearchText(current, searchText)), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        if (getSupportErrorCode(ticketsQuery.error) !== 'VALIDATION_ERROR') return;
        console.error('[SupportInbox] Gateway rejected the ticket list filter; the default filter is restored');
        setSearchText('');
        setFilter(DEFAULT_TICKET_LIST_FILTER);
    }, [ticketsQuery.error]);

    const selectedTicketUnread =
        ticketsQuery.data?.pages.flatMap(page => page.items).find(ticket => ticket.id === selectedTicketId)?.unread ??
        0;

    return {
        selectedTicketId,
        selectedTicketUnread,
        totalUnread,
        filter,
        searchText,
        changeSearchText: nextSearchText => {
            setSearchText(nextSearchText);
            if (nextSearchText === '') setFilter(current => applySearchText(current, ''));
        },
        changeStatusFilter: status => setFilter(current => ({ ...current, status })),
        resetFilter: () => {
            setSearchText('');
            setFilter(DEFAULT_TICKET_LIST_FILTER);
        },
        selectTicket: setSelectedTicketId,
        closeTicket: () => setSelectedTicketId(undefined),
    };
}

function applySearchText(filter: SupportTicketListFilter, searchText: string): SupportTicketListFilter {
    return filter.q === searchText ? filter : { ...filter, q: searchText };
}
